/**
 * Press & Media Kit — editable content.
 *
 * Everything a journalist / editor might quote lives in ONE file so
 * non-engineers can update it without touching JSX.
 *
 * ▶ For any asset you want to publish: drop the file into
 *   `/app/frontend/public/press/…` and flip its `available: true` here.
 *   Assets left `available: false` render disabled ("Coming soon") in the UI
 *   and are excluded from the downloadable ZIP + JSON-LD.
 *
 * ▶ Rule for missing information: leave the string EMPTY (""). The page
 *   renders an "Available on request" placeholder instead of fabricating.
 *   Never invent dates, quotes, coverage, awards, or user numbers here.
 */

export const pressConfig = {
  // -------------------------------------------------------------------
  // COMPANY QUICK FACTS
  // -------------------------------------------------------------------
  company: {
    legalName:     "Meet to Keep LLC",
    productName:   "Meet to Keep",
    industry:      "Online Dating / Dating Technology",
    founder:       "Manuel Santana",
    businessModel: "Freemium",
    platforms:     "iOS and Android",
    website:       "MeetToKeep.com",
    // Editable — leave empty if not yet public.
    founded:       "",
    launchDate:    "",
    headquarters:  "",
    mediaContact:  "",  // e.g. "Media Relations — press@meettokeep.com"
  },

  // -------------------------------------------------------------------
  // OFFICIAL PROFILES (JSON-LD sameAs). Leave empty strings out — only
  // add entries the site already publishes elsewhere.
  // -------------------------------------------------------------------
  sameAs: [
    // "https://www.linkedin.com/company/meet-to-keep",
    // "https://x.com/meettokeep",
    // "https://www.instagram.com/meettokeep",
  ],

  // -------------------------------------------------------------------
  // MEDIA CONTACT
  // -------------------------------------------------------------------
  mediaContact: {
    email: "",   // e.g. "press@meettokeep.com" — leave empty to render "Available on request"
    label: "Meet to Keep Media Relations",
  },

  // -------------------------------------------------------------------
  // FOUNDER
  // -------------------------------------------------------------------
  founder: {
    name:  "Manuel Santana",
    title: "Founder, Meet to Keep",
    shortBio: "Manuel Santana is a Dominican-American media personality, author of The 12 Laws of Dating Dynamics, and host of The Prove Your Point Podcast. He founded Meet to Keep to translate a decade of observing modern relationships into a dating platform built for lasting connections.",
    fullBio:  "Manuel Santana is the founder of Meet to Keep, a Dominican-American media personality, podcast host, and author focused on modern dating, relationships, gender dynamics, and culture.\n\nBorn in Santo Domingo, Dominican Republic, and raised in New York City after his family immigrated in 1989, Manuel came of age in one of the world's most diverse cultural environments. After high school, the Santana family relocated to Atlanta, where he entered the world of nightlife — promotion and security — and spent years observing thousands of social interactions unfold in real time. Night after night, he watched how attraction developed, how relationships formed, and how misunderstandings often destroyed connections.\n\nIn 2013, Manuel returned to the Northeast, settling in Boston. As music gave way to a new mission, he began studying dating dynamics, relationship psychology, and the growing disconnect between men and women in contemporary society. That study became The 12 Laws of Dating Dynamics and The Prove Your Point Podcast — a platform for the honest conversations he felt modern dating deserved.\n\nMeet to Keep is the next step of that mission: a dating app built around the \"Match → Meet → Keep\" loop, designed to help people move from screens to real, lasting relationships.",
    headshot: { available: true, src: "/press/founder-headshot.webp", alt: "Manuel Santana, Founder of Meet to Keep" },
    additionalPhotos: [
      // { available: false, src: "/press/founder-2.jpg", alt: "Manuel Santana speaking" },
    ],
  },

  // -------------------------------------------------------------------
  // APPROVED FOUNDER QUOTES  (never invent these)
  // -------------------------------------------------------------------
  quotes: [
    // Add approved quotes only. Structure:
    // { quote: "…", context: "on the founding vision" },
  ],

  // -------------------------------------------------------------------
  // APP SCREENSHOTS
  // Drop files in `/app/frontend/public/press/screenshots/` and flip
  // `available: true`. Keep filenames descriptive so alt text matches.
  // -------------------------------------------------------------------
  screenshots: [
    { available: false, src: "/press/screenshots/discover.png",       alt: "Meet to Keep Discover / Encounters screen showing profile cards" },
    { available: false, src: "/press/screenshots/profile.png",        alt: "Meet to Keep user profile screen" },
    { available: false, src: "/press/screenshots/likes.png",          alt: "Meet to Keep Likes screen" },
    { available: false, src: "/press/screenshots/matches.png",        alt: "Meet to Keep Matches list" },
    { available: false, src: "/press/screenshots/messaging.png",      alt: "Meet to Keep messaging thread" },
    { available: false, src: "/press/screenshots/compatibility.png",  alt: "Meet to Keep compatibility score view" },
    { available: false, src: "/press/screenshots/premium.png",        alt: "Meet to Keep Premium subscription screen" },
    { available: false, src: "/press/screenshots/safety.png",         alt: "Meet to Keep safety and verification screen" },
  ],

  // -------------------------------------------------------------------
  // LOGOS & BRAND ASSETS
  // -------------------------------------------------------------------
  logos: [
    // The primary wordmark is already public on the site — always available.
    { available: true,  label: "Primary wordmark (PNG)", src: "/logo-wordmark.png", alt: "Meet to Keep primary wordmark" },
    { available: false, label: "Wordmark (SVG)",         src: "/press/logos/meet-to-keep-wordmark.svg", alt: "Meet to Keep wordmark vector" },
    { available: false, label: "Black logo",             src: "/press/logos/meet-to-keep-black.png",    alt: "Meet to Keep logo in black" },
    { available: false, label: "White logo",             src: "/press/logos/meet-to-keep-white.png",    alt: "Meet to Keep logo in white" },
    { available: false, label: "App icon",               src: "/press/logos/meet-to-keep-app-icon.png", alt: "Meet to Keep app icon" },
  ],

  // -------------------------------------------------------------------
  // PRODUCT MOCKUPS (optional device-frame artwork)
  // -------------------------------------------------------------------
  mockups: [
    // { available: false, src: "/press/mockups/iphone-discover.png", alt: "iPhone showing Meet to Keep Discover" },
  ],

  // -------------------------------------------------------------------
  // PRESS RELEASES
  // Add releases as they publish. Nothing is invented here.
  // -------------------------------------------------------------------
  pressReleases: [
    // {
    //   headline:    "Meet to Keep announces …",
    //   date:        "2026-01-15",   // ISO or human-readable
    //   description: "One-sentence summary.",
    //   href:        "/press/releases/announcement.pdf",
    // },
  ],

  // -------------------------------------------------------------------
  // PRESS COVERAGE — never invent. Section is hidden entirely when empty.
  // -------------------------------------------------------------------
  coverage: [
    // {
    //   publication: "TechCrunch",
    //   logo:        "/press/coverage/techcrunch.svg",
    //   headline:    "…",
    //   date:        "2026-01-01",
    //   description: "…",
    //   href:        "https://techcrunch.com/…",
    // },
  ],

  // -------------------------------------------------------------------
  // DOWNLOADABLE MEDIA-KIT ZIP
  // Drop a real assembled zip at the src path; keep `available: false`
  // until the file exists to render "Coming soon" instead of a broken link.
  // -------------------------------------------------------------------
  mediaKitZip: {
    available: false,
    src:       "/press/Meet-to-Keep-Media-Kit.zip",
    filename:  "Meet-to-Keep-Media-Kit.zip",
  },
};
